import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSearchLocationPopularDestinations } from '@/constants/search';
import { usePopularParks } from '@/graphql/hooks/search/getPopularParks';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import useMapboxQuery from '@/hooks/useMapboxQuery';
import useQueryParams from '@/hooks/useQueryParams';
import { useRecentSearchesWithFilters } from '@/hooks/useRecentSearchesWithFilters';
import { useRouter } from '@/hooks/useRouter';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchAddressFilter } from '@/redux/selectors/globalHeader';
import { OptimizelyFlags, useOptimizelyDecision } from '@/services/experiments';
import { isDeliveryMode } from '@/utility/queryParams';

import { useNavigateToRecentSearch } from './recentSearch';

interface ISuggestion {
  label: string;
  value?: unknown;
  url?: string;
  sublabel?: string;
}

type TLocationFilterContext = {
  addressValue: string;
  setAddressValue: (value: string) => void;
  suggestions: ISuggestion[];
  recentSearches: ISuggestion[];
  popularDestinations: ISuggestion[];
  navigateToRecentSearch: (url: string) => void;
  setMapboxValues: (mapboxId: string, fullMapboxAddress: string) => void;
};

const LocationFilterContext = createContext<TLocationFilterContext>({
  addressValue: '',
  setAddressValue: () => undefined,
  suggestions: [],
  recentSearches: [],
  popularDestinations: [],
  navigateToRecentSearch: () => undefined,
  setMapboxValues: () => undefined,
});

export const useLocationFilterContext = () => useContext(LocationFilterContext);

const useSuggestions = (query: string) => {
  const places = useMapboxQuery(
    query.length < 3
      ? null
      : {
          text: query,
          types: 'country,region,postcode,district,place,locality,neighborhood,address',
        },
  );

  return places.map(p => ({
    label: 'place_name' in p ? p.place_name : p.name_preferred || p.name,
    sublabel: 'place_formatted' in p ? p.full_address || p.place_formatted : '',
    value: p,
  }));
};

const usePopularDestinations = () => {
  const searchLocationPopularDestinations = useSearchLocationPopularDestinations();
  const parksResponse = usePopularParks(searchLocationPopularDestinations);

  const popularDestinationsByLocation = parksResponse?.map(park => ({ label: park?.label }));

  return popularDestinationsByLocation.length
    ? popularDestinationsByLocation
    : searchLocationPopularDestinations;
};

interface TLocationFilterProviderProps {
  onNavigateToRecentSearch?: () => void;
}

export const LocationFilterProvider: React.FC<
  React.PropsWithChildren<TLocationFilterProviderProps>
> = ({ onNavigateToRecentSearch, children }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const queryParams = useQueryParams();
  const isRentalsVDeliverySearchModeEnabled = useOptimizelyDecision(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isCurrentlyDeliveryMode = isDeliveryMode(queryParams);

  const addressValue = useSelector(getHeaderSearchAddressFilter) || '';

  // Use GeoIP for delivery mode when no address is provided
  const { data: geoDataMyIp } = useGeoDataMyIp();

  useEffect(() => {
    // For delivery mode, prefill with GeoIP location if no address is set
    // Check both the addressValue and query params to ensure no address was actually provided
    const queryAddress = queryParams?.address;
    const hasActualAddress = addressValue || queryAddress;

    // Don't apply GeoIP on homepage - only on search results page (SRP)
    const isHomepage = router.pathname === '/';

    if (
      isRentalsVDeliverySearchModeEnabled &&
      isCurrentlyDeliveryMode &&
      !hasActualAddress &&
      !isHomepage &&
      geoDataMyIp?.city &&
      geoDataMyIp?.state
    ) {
      const geoLocation = `${geoDataMyIp.city}, ${geoDataMyIp.state}`;
      dispatch(
        setHeaderSearchFilter({
          address: geoLocation,
          manual_address_input: false,
        }),
      );
    }
  }, [
    dispatch,
    router.pathname,
    isRentalsVDeliverySearchModeEnabled,
    isCurrentlyDeliveryMode,
    addressValue,
    queryParams?.address,
    geoDataMyIp,
  ]);

  const setAddressValue = (value: string) => {
    dispatch(setHeaderSearchFilter({ address: value, manual_address_input: true }));
  };

  const setMapboxValues = (mapboxId: string, fullMapboxAddress: string) => {
    dispatch(
      setHeaderSearchFilter({ mapbox_id: mapboxId, full_mapbox_address: fullMapboxAddress }),
    );
  };

  const suggestions = useSuggestions(addressValue);
  const [recentSearches] = useRecentSearchesWithFilters();
  const popularDestinations = usePopularDestinations();

  const navigateToRecentSearch = useNavigateToRecentSearch(onNavigateToRecentSearch);

  return (
    <LocationFilterContext.Provider
      value={{
        addressValue,
        setAddressValue,
        suggestions,
        recentSearches,
        popularDestinations,
        navigateToRecentSearch,
        setMapboxValues,
      }}>
      {children}
    </LocationFilterContext.Provider>
  );
};
